/**
 * How a visitor arrived, and the three funnel steps the public pages report
 * (go-to-market blocker 7; backend: services/marketing/funnelAnalytics.ts).
 *
 * Captured once per page load from the address the visitor landed on and the
 * page that sent them, and held in this module's memory — **not** in
 * localStorage, sessionStorage or a cookie. That is the promise in the Privacy
 * Policy (§11): nothing is stored on the device. The price is stated rather
 * than hidden: a visitor who leaves and comes back tomorrow arrives as new,
 * and a reload loses the campaign tags of the first landing. `utils/planIntent`
 * keeps its choice in localStorage because it is the visitor's own choice of
 * plan; where somebody came from is ours to want, so it gets no such storage.
 *
 * What is never sent:
 *
 * - the blog article (`?src=<slug>` on a blog call to action) — whether there
 *   was one is all that leaves the page, because the article somebody read is
 *   a health inference (MAR-004 §6);
 * - the referring page's path or query — only its host;
 * - anything at all when the browser sends Global Privacy Control or Do Not
 *   Track, or once the visitor is signed in.
 */

export type FunnelStep = "landing" | "pricing" | "register_view";

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  /** A partner's referral code; the server keeps it only if it is real. */
  ref?: string;
  /** The referring host, external sites only. */
  referrer?: string;
  /** A blog link brought them here — never which article. */
  blog?: boolean;
  /** When this page load began, ISO 8601. */
  landedAt?: string;
}

const PARAMS = ["utm_source", "utm_medium", "utm_campaign", "ref"] as const;
const MAX_LENGTH = 100;

let captured: Attribution | null = null;

/** Global Privacy Control or Do Not Track. */
export function declinesTracking(): boolean {
  if (typeof navigator === "undefined") return true;
  const nav = navigator as Navigator & {
    globalPrivacyControl?: boolean;
    msDoNotTrack?: string;
  };
  const win =
    typeof window !== "undefined"
      ? (window as Window & { doNotTrack?: string })
      : undefined;
  return (
    nav.globalPrivacyControl === true ||
    nav.doNotTrack === "1" ||
    nav.msDoNotTrack === "1" ||
    win?.doNotTrack === "1"
  );
}

/**
 * Read the landing once. Later calls return the first capture: the visitor
 * moving from the home page to pricing inside the app is the same arrival.
 */
export function captureAttribution(
  location: {
    search: string;
    pathname: string;
    host: string;
  } = window.location,
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
  now: Date = new Date(),
): Attribution {
  if (captured) return captured;

  const result: Attribution = { landedAt: now.toISOString() };
  const query = new URLSearchParams(location.search);
  for (const name of PARAMS) {
    const value = query.get(name)?.trim();
    if (value) result[name] = value.slice(0, MAX_LENGTH);
  }

  let fromOwnBlog = false;
  if (referrer) {
    try {
      const url = new URL(referrer);
      if (url.host && url.host !== location.host) {
        result.referrer = url.host;
      } else if (url.pathname.startsWith("/blog")) {
        fromOwnBlog = true;
      }
    } catch {
      // A referrer that is not a URL is no referrer.
    }
  }

  if (
    query.get("src")?.trim() ||
    fromOwnBlog ||
    location.pathname.startsWith("/blog")
  ) {
    result.blog = true;
  }

  captured = result;
  return captured;
}

/** What the sign-up sends; undefined under GPC / DNT, so nothing is sent. */
export function getAttribution(): Attribution | undefined {
  if (declinesTracking()) return undefined;
  return captured ?? captureAttribution();
}

/** For tests. */
export function resetAttribution(): void {
  captured = null;
}

function apiBase(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) || "/api";
}

/**
 * Report one step. Fire-and-forget: the answer is always 204 and nothing on
 * the page waits for it. `sendBeacon` where there is one, because it survives
 * the page being left; `fetch` with keepalive otherwise.
 */
export function trackStep(step: FunnelStep, signedIn = false): void {
  if (signedIn || declinesTracking()) return;
  const attribution = getAttribution();
  if (!attribution) return;

  const { landedAt: _landedAt, ...arrival } = attribution;
  const body = JSON.stringify({ step, ...arrival });
  const url = `${apiBase()}/analytics/event`;

  try {
    if (typeof navigator.sendBeacon === "function") {
      const sent = navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" }),
      );
      if (sent) return;
    }
    // Plain fetch, not lib/http: this needs `keepalive`, which that client
    // does not pass through, and none of its interceptors — a beacon carries
    // no token, raises no toast and has no answer worth reading.
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    // A counter that cannot be reached costs a data point, never a page.
  }
}

/** The step a signed-out page reports, by path; null for every other page. */
export function stepForPath(pathname: string): FunnelStep | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return "landing";
  if (path === "/pricing") return "pricing";
  if (path === "/traveler/register") return "register_view";
  return null;
}
